/**
 * Card Helper Utilities
 * Functions for card positioning, rotation, and styling
 */

/**
 * Get card border color based on card type
 * @param {string} type - Card type
 * @returns {string} Tailwind border class
 */
export function getCardBorderColor(type) {
  const borderColors = {
    ally: 'border-[var(--ally-border)]',
    boss: 'border-[var(--boss-border)]',
    epic_boss: 'border-[var(--boss-border)]',
    legendary_artifact: 'border-[var(--legendary-border)]',
    artifact: 'border-[var(--artifact-border)]',
    curse: 'border-[var(--curse-border)]',
    event: 'border-[var(--event-border)]',
    maneuver: 'border-[var(--maneuver-border)]',
    quest: 'border-[var(--quest-border)]',
    epic_quest: 'border-[var(--quest-border)]',
    trap: 'border-[var(--trap-border)]',
    monster: 'border-[var(--blood-red)]',
  };

  return borderColors[type] || 'border-[var(--border-dark)]';
}

export function getCardRingColor(type) {
  const borderColors = {
    ally: 'ring-[var(--ally-border)]',
    boss: 'ring-[var(--boss-border)]',
    epic_boss: 'ring-[var(--boss-border)]',
    legendary_artifact: 'ring-[var(--legendary-border)]',
    artifact: 'ring-[var(--artifact-border)]',
    curse: 'ring-[var(--curse-border)]',
    event: 'ring-[var(--event-border)]',
    maneuver: 'ring-[var(--maneuver-border)]',
    quest: 'ring-[var(--quest-border)]',
    epic_quest: 'border-[var(--quest-border)]',
    trap: 'ring-[var(--trap-border)]',
    monster: 'ring-[var(--blood-red)]',
  };

  return borderColors[type] || 'border-[var(--border-dark)]';
}

/**
 * Get card background gradient based on card type
 * @param {string} type - Card type
 * @returns {string} Tailwind gradient class
 */
export function getCardBackground(type) {
  const backgrounds = {
    ally: 'bg-gradient-to-br from-[#0D2818] to-[var(--bg-tertiary)]',
    boss: 'bg-gradient-to-br from-[#2D0A0A] to-[var(--bg-tertiary)]',
    epic_boss: 'bg-gradient-to-br from-[#2D0A0A] to-[var(--bg-tertiary)]',
    legendary_artifact: 'bg-gradient-to-br from-[#2D2006] to-[var(--bg-tertiary)]',
    normal_artifact: 'bg-gradient-to-br from-[#1F1F24] to-[var(--bg-tertiary)]',
    curse: 'bg-gradient-to-br from-[#1A0D2E] to-[var(--bg-tertiary)]',
    event: 'bg-gradient-to-br from-[#0A1D24] to-[var(--bg-tertiary)]',
    maneuver: 'bg-gradient-to-br from-[#2D1A06] to-[var(--bg-tertiary)]',
    quest: 'bg-gradient-to-br from-[#2D2506] to-[var(--bg-tertiary)]',
    epic_quest: 'bg-gradient-to-br from-[#2D2506] to-[var(--bg-tertiary)]',
    trap: 'bg-gradient-to-br from-[#1A1C20] to-[var(--bg-tertiary)]',
  };

  return backgrounds[type] || 'bg-[var(--bg-tertiary)]';
}

/**
 * Calculate card position in a fan layout
 * @param {number} index - Card index
 * @param {number} totalCards - Total number of cards
 * @param {number} spread - Horizontal spread in pixels (default: 60)
 * @returns {{ x: number, y: number }} Position
 */
export function getCardPosition(index, totalCards = 5, spread = 60) {
  const centerOffset = ((totalCards - 1) / 2) * spread;
  const x = index * spread - centerOffset;
  const y = Math.abs(index - (totalCards - 1) / 2) * 10; // Arc effect

  return { x, y };
}

/**
 * Calculate card rotation in a fan layout
 * @param {number} index - Card index
 * @param {number} totalCards - Total number of cards
 * @param {number} maxRotation - Maximum rotation angle (default: 15)
 * @returns {number} Rotation angle in degrees
 */
export function getCardRotation(index, totalCards = 5, maxRotation = 15) {
  const center = (totalCards - 1) / 2;
  return (index - center) * (maxRotation / center);
}

/**
 * Get card image path
 * @param {string} type - Card type
 * @param {string} id - Card ID or slug
 * @returns {string} Image path
 */
export function getCardImage(type, id) {
  // Convert card type to folder name
  const folderMap = {
    ally: 'allies',
    boss: 'epic_bosses',
    epic_boss: 'epic_bosses',
    legendary_artifact: 'legendary_artifacts',
    normal_artifact: 'normal_artifacts',
    curse: 'curses',
    event: 'events',
    maneuver: 'maneuvers',
    quest: 'quests',
    epic_quest: 'epic_quests',
    trap: 'traps',
  };

  const folder = folderMap[type] || type;
  return `/assets/cards/${folder}/${id}.webp`;
}

/**
 * Check if card type is a creature/character
 * @param {string} type - Card type
 * @returns {boolean}
 */
export function isCreatureCard(type) {
  return ['ally', 'boss', 'epic_boss'].includes(type);
}

/**
 * Check if card type is an artifact
 * @param {string} type - Card type
 * @returns {boolean}
 */
export function isArtifactCard(type) {
  return ['legendary_artifact', 'normal_artifact'].includes(type);
}

/** Punkty Chwały za zniszczenie Legendarnego Artefaktu (backend: ArtifactSalvage.js). */
export const LEGENDARY_SALVAGE_FAME = 4;

/**
 * Co gracz dostanie za zniszczenie tego Artefaktu: Legendarny → 4 Punkty Chwały,
 * zwykły → połowa ceny w Złocie (w GÓRĘ, do puli tury). Lustro reguły z backendu —
 * `backend/services/ArtifactSalvage.js`. Zmiana tam wymaga zmiany tutaj.
 *
 * @param {Object} card - Pełna karta z cardContext
 * @returns {null|{fame: number, gold: number}} null, gdy nie ma czego wypłacić
 */
export function getArtifactSalvageReward(card) {
  if (!card || card.type !== 'artifact') return null;
  if (card.subtype === 'legendary') return { fame: LEGENDARY_SALVAGE_FAME, gold: 0 };
  const goldCost = typeof card.cost === 'number' ? card.cost : (card.cost?.gold || 0);
  const gold = Math.ceil(goldCost / 2);
  // Linia ewolucji Smoka nie ma ceny — nie ma z czego liczyć połowy.
  return gold > 0 ? { fame: 0, gold } : null;
}

/**
 * Get resource icon emoji
 * @param {string} resource - Resource type
 * @returns {string} Emoji
 */
export function getResourceIcon(resource) {
  const icons = {
    strength: '⚔️',
    knowledge: '📖',
    gold: '💰',
    fame: '⭐',
  };

  return icons[resource] || '';
}
